import { InputHTMLAttributes, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      className = '',
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-[var(--text)] mb-2">
            {label}
            {required && <span className="text-secondary ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-text-light">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            className={`
              block w-full rounded-lg border-2 px-4 py-3
              bg-[var(--surface)] text-[var(--text)] placeholder-[var(--text-muted)]
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-0
              disabled:opacity-50 disabled:cursor-not-allowed
              ${leftIcon ? 'pl-10' : ''}
              ${rightIcon ? 'pr-10' : ''}
              ${
                error
                  ? 'border-[var(--danger)] focus:border-[var(--danger)] focus:ring-[var(--danger)]'
                  : 'border-[var(--border)] focus:border-[var(--accent)] focus:ring-[var(--accent)]'
              }
              ${className}
            `}
            disabled={disabled}
            required={required}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-text-light">
              {rightIcon}
            </div>
          )}
        </div>

        {error && <p className="mt-2 text-sm text-[var(--danger)]">{error}</p>}

        {!error && helperText && <p className="mt-2 text-sm text-text-light">{helperText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
